'use client';

import * as React from 'react';
import {
  DayPicker,
  getDefaultClassNames,
  useDayPicker,
  type CustomComponents,
  type DayButton,
  type DayPickerProps,
  type Modifiers,
} from 'react-day-picker';

import { IconButton } from '../icon-button/icon-button';
import { cn } from '../../lib/utils';
import { ChevronDown, ChevronLeft, ChevronRight } from '../../icons';
import { Icon } from '../icon/icon';

// Day cell size, which also sets the week row height. Matches `--cell-size`.
export const CALENDAR_CELL_SIZE_PX = 40;
export const CALENDAR_WEEK_STARTS_ON = 1 satisfies DayPickerProps['weekStartsOn'];

type DayPickerLocale = DayPickerProps['locale'];

type DayPickerLocaleWithCode = DayPickerLocale & { code?: string };

/**
 * Provides locale code from a DayPicker locale object.
 *
 * Why:
 * - Using `locale?.code` directly is causing TypeScript errors as we have date-fns v2 and v4 typings in the project.
 * - This helper bridges the type conflict at the type level. It does not alter runtime behavior.
 *
 * The DayPicker API and props remain unchanged.
 */
const getLocaleCode = (locale: DayPickerLocale): string | undefined => {
  if (!locale) return undefined;
  const localeWithCode: DayPickerLocaleWithCode = locale;
  return localeWithCode.code;
};

// A single day range is both start and end, so it gets no band
const getRangePosition = (modifiers: Modifiers) => {
  const { range_start: isStart, range_end: isEnd, range_middle: isMiddle } = modifiers;

  if (isStart && isEnd) return 'single';
  if (isStart) return 'start';
  if (isEnd) return 'end';
  if (isMiddle) return 'middle';
  return undefined;
};

// An id rather than a name, so the dropdown is never submitted with a surrounding form
const CalendarSelect = (props: React.ComponentProps<'select'>) => (
  <select id={React.useId()} {...props} />
);

// Module level, so the slots keep their identity and the picker never remounts on a re-render
const calendarComponents: Partial<CustomComponents> = {
  Root: ({ className, rootRef, ...props }) => (
    <div data-slot="calendar" ref={rootRef} className={cn(className)} {...props} />
  ),
  Select: CalendarSelect,
  Day: ({ day: _day, modifiers, ...props }) => (
    <td data-range={getRangePosition(modifiers)} {...props} />
  ),
  // the picker marks the arrows aria-disabled at the first or last allowed month, so a
  // focused arrow keeps keyboard focus there
  PreviousMonthButton: ({ children: _chevron, ...props }) => (
    <IconButton variant="white" size="sm" icon={ChevronLeft} {...props} />
  ),
  NextMonthButton: ({ children: _chevron, ...props }) => (
    <IconButton variant="white" size="sm" icon={ChevronRight} {...props} />
  ),
  Chevron: ({ className, orientation, ...props }) => {
    if (orientation === 'left') {
      return <Icon icon={ChevronLeft} className={cn('size-4', className)} {...props} />;
    }

    if (orientation === 'right') {
      return <Icon icon={ChevronRight} className={cn('size-4', className)} {...props} />;
    }

    return <Icon icon={ChevronDown} className={cn('size-4', className)} {...props} />;
  },
  DayButton: CalendarDayButton,
  WeekNumber: ({ children, ...props }) => (
    <td {...props}>
      <div className="flex size-(--cell-size) items-center justify-center text-center">
        {children}
      </div>
    </td>
  ),
};

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  locale,
  formatters,
  components,
  ...props
}: DayPickerProps) {
  const defaultClassNames = getDefaultClassNames();
  const localeCode = getLocaleCode(locale);

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        'group/calendar bg-white p-3 [--cell-radius:var(--radius-md)] [--cell-size:--spacing(10)] in-data-[slot=card-content]:bg-transparent in-data-[slot=popover-content]:bg-transparent',
        className,
      )}
      captionLayout={captionLayout}
      locale={locale}
      formatters={{
        formatMonthDropdown: (date) => date.toLocaleString(localeCode, { month: 'short' }),
        ...formatters,
      }}
      classNames={{
        root: cn('w-fit', defaultClassNames.root),
        months: cn('flex gap-4 flex-col md:flex-row relative', defaultClassNames.months),
        month: cn('flex flex-col w-full gap-4', defaultClassNames.month),
        nav: cn(
          'flex items-center gap-1 w-full absolute top-0 inset-x-0 justify-between',
          defaultClassNames.nav,
        ),
        button_previous: cn('select-none', defaultClassNames.button_previous),
        button_next: cn('select-none', defaultClassNames.button_next),
        month_caption: cn(
          'flex items-center justify-center h-8 w-full px-(--cell-size)',
          defaultClassNames.month_caption,
        ),
        dropdowns: cn(
          'w-full flex items-center typo-sm-semibold justify-center h-8 gap-2',
          defaultClassNames.dropdowns,
        ),
        dropdown_root: cn(
          'relative cn-calendar-dropdown-root rounded-(--cell-radius)',
          // the select is invisible, so its focus ring goes on the root, matching the nav buttons
          'has-focus-visible:ring-[3px] has-focus-visible:ring-focus-ring has-focus-visible:ring-offset-2',
          defaultClassNames.dropdown_root,
        ),
        dropdown: cn('absolute bg-white inset-0 opacity-0', defaultClassNames.dropdown),
        // dropdown chevrons take the same 32px area as the small icon button
        caption_label: cn(
          'select-none typo-sm-semibold',
          captionLayout !== 'label' &&
            'cn-calendar-caption-label rounded-(--cell-radius) flex items-center [&>svg]:size-8 [&>svg]:p-2 [&>svg]:text-space',
          defaultClassNames.caption_label,
        ),
        // margin, as a collapsed-border table ignores padding
        month_grid: cn('mx-3 border-collapse', defaultClassNames.month_grid),
        weekdays: cn('flex', defaultClassNames.weekdays),
        weekday: cn(
          'flex h-(--cell-size) flex-1 items-center justify-center typo-sm-semibold text-text-primary select-none',
          defaultClassNames.weekday,
        ),
        week: cn('flex w-full', defaultClassNames.week),
        week_number_header: cn('select-none w-(--cell-size)', defaultClassNames.week_number_header),
        week_number: cn('text-[0.8rem] select-none text-space-60', defaultClassNames.week_number),
        day: cn(
          // 1px padding keeps every circle and the band 1px inside the cell, as in the designs
          'relative w-full rounded-full h-full p-px bg-clip-content text-center group/day aspect-square select-none not-data-disabled:hover:bg-space-04',
          // range band behind the day buttons, sized by the day's position in the range
          'before:absolute before:inset-y-px before:bg-surface-tertiary',
          'data-[range=start]:before:left-1/2 data-[range=start]:before:right-0',
          'data-[range=middle]:before:inset-x-0',
          'data-[range=end]:before:left-0 data-[range=end]:before:right-1/2',
          defaultClassNames.day,
        ),
        range_start: defaultClassNames.range_start,
        range_middle: defaultClassNames.range_middle,
        range_end: defaultClassNames.range_end,
        today: cn(
          // a disabled today keeps only its indigo text
          'not-data-disabled:bg-space-04 rounded-full data-[selected=true]:rounded-full',
          defaultClassNames.today,
        ),
        outside: cn('text-space-40 aria-selected:text-space-40', defaultClassNames.outside),
        disabled: cn('text-space-40', defaultClassNames.disabled),
        hidden: cn('invisible', defaultClassNames.hidden),
        ...classNames,
      }}
      components={{ ...calendarComponents, ...components }}
      weekStartsOn={CALENDAR_WEEK_STARTS_ON}
      {...props}
    />
  );
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const defaultClassNames = getDefaultClassNames();
  const { dayPickerProps } = useDayPicker();
  const localeCode = getLocaleCode(dayPickerProps.locale);

  const ref = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    if (modifiers['focused']) ref.current?.focus();
  }, [modifiers]);

  // A selected single day, or the start or end of a range
  const isFilled = Boolean(modifiers['selected'] && !modifiers['range_middle']);

  return (
    <button
      ref={ref}
      data-day={day.date.toLocaleDateString(localeCode)}
      data-filled={isFilled}
      data-range-middle={modifiers['range_middle']}
      data-highlighted={modifiers['highlighted']}
      data-today={modifiers['today']}
      className={cn(
        // layout + spacing
        'relative isolate z-10 flex aspect-square size-auto w-full min-w-[calc(var(--cell-size)-2px)] flex-col justify-center gap-1 border-0',
        'rounded-full',
        // typography
        'typo-sm leading-none [&>span]:text-xs [&>span]:opacity-70',
        // selected day and range start and end are full circles on top of the band
        'data-[filled=true]:bg-indigo-crush data-[filled=true]:text-white data-[filled=true]:hover:bg-indigo-crush-80',
        // middle days show a hover circle on top of the band, as tapping them changes the range
        'data-[range-middle=true]:hover:bg-indigo-crush-20',
        // days a feature marks with `modifiers={{ highlighted }}`, e.g. days with showround slots
        'data-[highlighted=true]:data-[filled=false]:bg-surface-tertiary data-[highlighted=true]:data-[filled=false]:hover:bg-indigo-crush-20',
        // today is semibold in every state, and indigo unless filled
        'data-[today=true]:font-semibold data-[today=true]:data-[filled=false]:text-indigo-crush',
        // focus states
        // "group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:border-focus-ring group-data-[focused=true]/day:ring-[3px] group-data-[focused=true]/day:ring-focus-ring/50",
        // focus state with focus-visible
        'focus-visible:relative focus-visible:z-10 focus-visible:border-focus-ring focus-visible:ring-[3px] focus-visible:ring-focus-ring/50',
        defaultClassNames.day,
        className,
      )}
      {...props}
    />
  );
}

export { Calendar, CalendarDayButton };
